import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { User, Task, Category, Project, Case, TaskFormData, ProjectFormData } from '../types';
import { debugLog } from '../utils/debug';
import { AUTH_SESSION_EXPIRED_EVENT } from '../utils/authSession';
import { getApiErrorMessage } from '../utils/apiError';
import { 
  authAPI, 
  tasksAPI, 
  categoriesAPI, 
  projectsAPI, 
  casesAPI, // ✅ DODANO: Cases API
  setAuthToken, 
  removeAuthToken, 
  setUser, 
  getUser, 
  getAuthToken 
} from '../services/api';

interface AppState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  tasks: Task[];
  cases: Case[]; // ✅ DODANO: Sprawy
  categories: Category[];
  projects: Project[];
  users?: Array<{ id: number; email: string; name?: string; role?: string }>;
  error: string | null;
  isAuthReady: boolean;
}

type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_AUTH_READY'; payload: boolean }
  | { type: 'LOGOUT' }
  | { type: 'SET_TASKS'; payload: Task[] }
  | { type: 'SET_CASES'; payload: Case[] }
  | { type: 'SET_CATEGORIES'; payload: Category[] }
  | { type: 'SET_PROJECTS'; payload: Project[] }
  | { type: 'SET_USERS'; payload: Array<{ id: number; email: string; name?: string; role?: string }> }
  | { type: 'ADD_TASK'; payload: Task }
  | { type: 'UPDATE_TASK'; payload: Task }
  | { type: 'DELETE_TASK'; payload: number }
  | { type: 'ADD_CATEGORY'; payload: Category }
  | { type: 'UPDATE_CATEGORY'; payload: Category }
  | { type: 'DELETE_CATEGORY'; payload: number }
  | { type: 'ADD_PROJECT'; payload: Project }
  | { type: 'UPDATE_PROJECT'; payload: Project }
  | { type: 'DELETE_PROJECT'; payload: number };

type TaskLoadParams = Parameters<typeof tasksAPI.getTasks>[0];
type TaskMutation = TaskFormData;
type TaskUpdateMutation = Record<string, unknown>;
type CategoryMutation = Partial<Category> & { name: string; color?: string };

const initialToken = getAuthToken();

const initialState: AppState = {
  user: initialToken ? getUser() : null,
  isAuthenticated: !!initialToken,
  isAuthReady: !initialToken,
  isLoading: false,
  tasks: [],
  cases: [], // ✅ DODANO: Sprawy
  categories: [],
  projects: [],
  users: [],
  error: null,
};

const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => void;
  loadTasks: (params?: TaskLoadParams) => Promise<void>;
  loadCases: () => Promise<void>; // ✅ DODANO: Ładowanie spraw
  loadCategories: () => Promise<void>;
  loadProjects: () => Promise<void>;
  loadUsers: () => Promise<void>;
  createTask: (taskData: TaskMutation) => Promise<void>;
  updateTask: (id: number, taskData: TaskUpdateMutation) => Promise<void>;
  updateTaskStatus: (id: number, status: Task['status']) => Promise<void>;
  deleteTask: (id: number) => Promise<void>;
  createCategory: (categoryData: CategoryMutation) => Promise<void>;
  updateCategory: (id: number, categoryData: Partial<CategoryMutation>) => Promise<void>;
  deleteCategory: (id: number) => Promise<void>;
  createProject: (projectData: ProjectFormData) => Promise<void>;
  updateProject: (id: number, projectData: Partial<ProjectFormData>) => Promise<void>;
  deleteProject: (id: number) => Promise<void>;
  clearError: () => void;
} | null>(null);

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    
    case 'SET_USER':
      return { 
        ...state, 
        user: action.payload, 
        isAuthenticated: !!action.payload,
        isAuthReady: true,
        isLoading: false 
      };

    case 'SET_AUTH_READY':
      return {
        ...state,
        isAuthReady: action.payload
      };
    
    case 'LOGOUT':
      return { 
        ...state, 
        user: null, 
        isAuthenticated: false, 
        tasks: [], 
        cases: [], // ✅ DODANO: Sprawy
        categories: [],
        projects: [],
        error: null,
        isAuthReady: true
      };
    
    case 'SET_TASKS':
      return { ...state, tasks: action.payload, isLoading: false };
    
    case 'SET_CASES': // ✅ DODANO: Sprawy
      return { ...state, cases: action.payload, isLoading: false };
    
    case 'ADD_TASK':
      return { ...state, tasks: [action.payload, ...state.tasks] };
    
    case 'UPDATE_TASK':
      return {
        ...state,
        tasks: state.tasks.map(task =>
          task.id === action.payload.id ? action.payload : task
        ),
      };
    
    case 'DELETE_TASK':
      return {
        ...state,
        tasks: state.tasks.filter(task => task.id !== action.payload),
      };
    
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload };
    case 'SET_USERS':
      return { ...state, users: action.payload };
    
    case 'ADD_CATEGORY':
      return { ...state, categories: [...state.categories, action.payload] };
    
    case 'UPDATE_CATEGORY':
      return {
        ...state,
        categories: state.categories.map(category =>
          category.id === action.payload.id ? action.payload : category
        ),
      };
    
    case 'DELETE_CATEGORY':
      return {
        ...state,
        categories: state.categories.filter(category => category.id !== action.payload),
      };
    
    case 'SET_PROJECTS':
      return { ...state, projects: action.payload };
    
    case 'ADD_PROJECT':
      return { ...state, projects: [...state.projects, action.payload] };
    
    case 'UPDATE_PROJECT':
      return {
        ...state,
        projects: state.projects.map(project =>
          project.id === action.payload.id ? action.payload : project
        ),
      };
    
    case 'DELETE_PROJECT':
      return {
        ...state,
        projects: state.projects.filter(project => project.id !== action.payload),
      };
    
    default:
      return state;
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Funkcje akcji
  const login = async (email: string, password: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });
      
      const response = await authAPI.login({ email, password });
      
      if (response.success) {
        setAuthToken(response.token);
        setUser(response.user);
        dispatch({ type: 'SET_USER', payload: response.user });
        // Załaduj dane użytkownika w tle.
        // Nie blokuj logowania, a listę użytkowników ładuj tylko dla admina.
        const loaders: Array<Promise<void>> = [
          loadTasks(),
          loadCases(),
          loadCategories(),
          loadProjects()
        ];
        if (response.user?.role === 'admin') {
          loaders.push(loadUsers());
        }
        // Uruchom bez await, aby ewentualne błędy nie zatrzymały logowania.
        Promise.allSettled(loaders).catch(() => {/* ciche */});
      } else {
        throw new Error(response.message);
      }
      
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Błąd logowania');
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const register = async (email: string, password: string, name?: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });
      
      const response = await authAPI.register({ email, password, name });
      
      if (response.success) {
        setAuthToken(response.token);
        setUser(response.user);
        dispatch({ type: 'SET_USER', payload: response.user });
        // Załaduj dane użytkownika w tle (bez blokowania rejestracji)
        const loaders: Array<Promise<void>> = [
          loadTasks(),
          loadCases(),
          loadCategories(),
          loadProjects()
        ];
        if (response.user?.role === 'admin') {
          loaders.push(loadUsers());
        }
        Promise.allSettled(loaders).catch(() => {/* ciche */});
      } else {
        throw new Error(response.message);
      }
      
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Błąd rejestracji');
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const logout = () => {
    removeAuthToken();
    dispatch({ type: 'LOGOUT' });
  };

  const loadTasks = useCallback(async (params?: TaskLoadParams) => {
    try {
      debugLog('[AppContext] Ładowanie zadań...', { params });
      const response = await tasksAPI.getTasks(params);
      debugLog('[AppContext] Response zadań:', response);
      if (response.success) {
        debugLog('[AppContext] Ustawiam zadania:', response.tasks?.length || 0, 'zadań');
        dispatch({ type: 'SET_TASKS', payload: response.tasks || [] });
      }
    } catch (error: unknown) {
      console.error('❌ [AppContext] Błąd ładowania zadań:', error);
      const message = getApiErrorMessage(error, 'Błąd ładowania zadań');
      dispatch({ type: 'SET_ERROR', payload: message });
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      debugLog('Ładowanie kategorii...');
      const response = await categoriesAPI.getCategories();
      debugLog('Response kategorii:', response);
      if (response.success) {
        debugLog('Ustawiam kategorie:', response.categories || []);
        dispatch({ type: 'SET_CATEGORIES', payload: response.categories || [] });
      }
    } catch (error: unknown) {
      console.error('❌ Błąd ładowania kategorii:', error);
      const message = getApiErrorMessage(error, 'Błąd ładowania kategorii');
      dispatch({ type: 'SET_ERROR', payload: message });
    }
  }, []);

  const loadCases = useCallback(async () => {
    try {
      debugLog('[AppContext] Ładowanie spraw...');
      const response = await casesAPI.getCases();
      debugLog('[AppContext] Response spraw:', response);
      if (response.success) {
        debugLog('[AppContext] Ustawiam sprawy:', response.cases?.length || 0, 'spraw');
        dispatch({ type: 'SET_CASES', payload: response.cases || [] });
      }
    } catch (error: unknown) {
      console.error('❌ [AppContext] Błąd ładowania spraw:', error);
      const message = getApiErrorMessage(error, 'Błąd ładowania spraw');
      dispatch({ type: 'SET_ERROR', payload: message });
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      debugLog('Ładowanie projektów...');
      const response = await projectsAPI.getProjects();
      debugLog('Response projektów:', response);
      if (response.success) {
        debugLog('Ustawiam projekty:', response.projects || []);
        dispatch({ type: 'SET_PROJECTS', payload: response.projects || [] });
      }
    } catch (error: unknown) {
      console.error('❌ Błąd ładowania projektów:', error);
      const message = getApiErrorMessage(error, 'Błąd ładowania projektów');
      dispatch({ type: 'SET_ERROR', payload: message });
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const resp = await fetch('/api/users', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      if (resp.ok) {
        const data = await resp.json();
        const list = data?.users || [];
        dispatch({ type: 'SET_USERS', payload: list });
      }
    } catch (e) {
      console.error('❌ Błąd ładowania użytkowników:', e);
    }
  }, []);

  const createTask = async (taskData: TaskMutation) => {
    try {
      const response = await tasksAPI.createTask(taskData);
      if (response.success && response.task) {
        dispatch({ type: 'ADD_TASK', payload: response.task });
      }
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Błąd tworzenia zadania');
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const updateTask = async (id: number, taskData: TaskUpdateMutation) => {
    debugLog('AppContext.updateTask:', { id, taskData });
    try {
      const response = await tasksAPI.updateTask(id, taskData);
      debugLog('AppContext.updateTask response:', response);
      if (response.success && response.task) {
        dispatch({ type: 'UPDATE_TASK', payload: response.task });
        debugLog('AppContext.updateTask: Task updated');
      }
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Błąd aktualizacji zadania');
      debugLog('AppContext.updateTask error:', { error, message });
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const updateTaskStatus = async (id: number, status: Task['status']) => {
    try {
      const response = await tasksAPI.updateTaskStatus(id, status);
      if (response.success && response.task) {
        dispatch({ type: 'UPDATE_TASK', payload: response.task });
      }
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Błąd aktualizacji statusu zadania');
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const deleteTask = async (id: number) => {
    debugLog('AppContext.deleteTask:', { id });
    try {
      await tasksAPI.deleteTask(id);
      dispatch({ type: 'DELETE_TASK', payload: id });
      debugLog('AppContext.deleteTask: Task archived');
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Błąd usuwania zadania');
      debugLog('AppContext.deleteTask error:', { error, message });
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const createCategory = async (categoryData: CategoryMutation) => {
    try {
      debugLog('Tworzenie kategorii:', categoryData);
      const response = await categoriesAPI.createCategory(categoryData);
      debugLog('Response tworzenia:', response);
      if (response.success && response.category) {
        debugLog('Dodano kategorię:', response.category);
        dispatch({ type: 'ADD_CATEGORY', payload: response.category });
      }
    } catch (error: unknown) {
      console.error('❌ Błąd tworzenia kategorii:', error);
      const message = getApiErrorMessage(error, 'Błąd tworzenia kategorii');
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const updateCategory = async (id: number, categoryData: Partial<CategoryMutation>) => {
    try {
      const response = await categoriesAPI.updateCategory(id, categoryData);
      if (response.success && response.category) {
        dispatch({ type: 'UPDATE_CATEGORY', payload: response.category });
      }
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Błąd aktualizacji kategorii');
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const deleteCategory = async (id: number) => {
    try {
      await categoriesAPI.deleteCategory(id);
      dispatch({ type: 'DELETE_CATEGORY', payload: id });
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Błąd usuwania kategorii');
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const createProject = async (projectData: ProjectFormData) => {
    try {
      const response = await projectsAPI.createProject(projectData);
      if (response.success && response.project) {
        dispatch({ type: 'ADD_PROJECT', payload: response.project });
      }
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Błąd tworzenia projektu');
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const updateProject = async (id: number, projectData: Partial<ProjectFormData>) => {
    try {
      const response = await projectsAPI.updateProject(id, projectData);
      if (response.success && response.project) {
        dispatch({ type: 'UPDATE_PROJECT', payload: response.project });
      }
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Błąd aktualizacji projektu');
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const deleteProject = async (id: number) => {
    try {
      await projectsAPI.deleteProject(id);
      dispatch({ type: 'DELETE_PROJECT', payload: id });
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Błąd usuwania projektu');
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Weryfikacja tokenu przy starcie aplikacji
  useEffect(() => {
    const handleSessionExpired = () => {
      dispatch({ type: 'LOGOUT' });
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);

    const verifyToken = async () => {
      const token = getAuthToken();
      debugLog('AppContext: Sprawdzanie tokenu przy starcie:', { 
        hasToken: !!token, 
        hasUser: !!state.user,
        isAuthenticated: state.isAuthenticated
      });
      
      if (!token) {
        dispatch({ type: 'SET_AUTH_READY', payload: true });
        return;
      }

      debugLog('AppContext: Weryfikowanie tokenu...');
      try {
        const response = await authAPI.verify();
        debugLog('AppContext: Token valid:', response);
        if (response.valid) {
          setUser(response.user);
          dispatch({ type: 'SET_USER', payload: response.user });
          debugLog('AppContext: Ładowanie danych...');
          // Ładuj dane w tle, nie wylogowuj przy błędach modułów
          Promise.allSettled([loadTasks(), loadCases(), loadCategories(), loadProjects()])
            .catch(() => {/* ciche */});
        } else {
          debugLog('AppContext: Token nieważny');
          logout();
        }
      } catch (error) {
        debugLog('AppContext: Błąd weryfikacji tokenu:', error);
        logout();
      }
    };

    verifyToken();
    return () => {
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    state,
    dispatch,
    login,
    register,
    logout,
    loadTasks,
    loadCases, // ✅ DODANO: Ładowanie spraw
    loadCategories,
    loadProjects,
    loadUsers,
    createTask,
    updateTask,
    updateTaskStatus,
    deleteTask,
    createCategory,
    updateCategory,
    deleteCategory,
    createProject,
    updateProject,
    deleteProject,
    clearError,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
